import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Cell, PieChart, Pie, RadialBarChart, RadialBar,
  AreaChart, Area,
} from 'recharts';
import dashboardService from '../../services/dashboardService';

/**
 * DashboardCharts — polished Recharts data-viz section for the operator
 * Dashboard. Three cards, ESS parity but fancier:
 *
 *   1. Revenue history  — 12-month bar chart (gradient, rounded bars) with
 *      MoM / YoY trend chips. Source: GET /dashboard/revenue-history.
 *   2. Aging            — donut (inner radius) with the total $ in the
 *      centre, graded soft→deep-red palette. Source:
 *      GET /dashboard/aging-buckets (segments reconcile to the Past-due KPI).
 *   3. Occupancy        — radial gauge of the CURRENT occupancy %. No
 *      occupancy time-series is stored, so we render current-state only
 *      (an honest gauge, not a fabricated trend) plus a unit breakdown.
 *
 * Colours derive from the live theme: chart accents read `var(--c-brand)`
 * so they track the blue/green/orange brand switcher, and the card chrome
 * reuses the dashboard's CSS tokens so dark mode flips automatically.
 */

// Theme tokens (resolved by the browser to the active brand / mode).
const INK = 'var(--c-ink)';
const INK3 = 'var(--c-ink3)';
const SURFACE = 'var(--c-surface)';
const BG = 'var(--c-bg)';
const BORDER = 'var(--c-border)';
const BRAND = 'var(--c-brand)';
const SUCCESS = 'var(--c-success)';
const DANGER = 'var(--c-danger)';

const tabular = { fontVariantNumeric: 'tabular-nums' };

const fmtMoney = (n) =>
  n == null ? '$0' : new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(n);

const fmtMoneyExact = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 2,
  }).format(Number(n || 0));

// "2026-01" -> "Jan" (with a year tick when January, for a 12-month axis).
const monthLabel = (key) => {
  if (!key) return '';
  const [y, m] = String(key).split('-');
  const idx = Math.max(0, Math.min(11, Number(m) - 1));
  const name = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][idx];
  return m === '01' ? `${name} '${String(y).slice(2)}` : name;
};

// "2026-05" -> "May 2026" (full label for the comparison footers).
const monthLabelFull = (key) => {
  if (!key) return '';
  const [y, m] = String(key).split('-');
  const name = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'][
    Math.max(0, Math.min(11, Number(m) - 1))];
  return `${name} ${y}`;
};
// "2026-06" -> "2026-05" (prior month) / "2025-06" (same month last year).
const priorMonthKey = (key) => {
  let [y, m] = String(key || '').split('-').map(Number);
  if (!y || !m) return '';
  m -= 1; if (m < 1) { m = 12; y -= 1; }
  return `${y}-${String(m).padStart(2, '0')}`;
};
const yearAgoKey = (key) => {
  const [y, m] = String(key || '').split('-');
  return y && m ? `${Number(y) - 1}-${m}` : '';
};

// ESS-style comparison footer: a row of ↑/↓ % vs a named prior period
// (e.g. "From May 2026" / "From June 2025"), shown under a trend chart.
const ComparisonFooter = ({ items }) => {
  const shown = (items || []).filter((it) => it && it.pct != null);
  if (!shown.length) return null;
  return (
    <div
      className="flex items-center justify-around flex-shrink-0"
      style={{ marginTop: 6, paddingTop: 8, borderTop: `0.5px solid ${BORDER}` }}
    >
      {shown.map((it, i) => (
        <div key={i} className="text-center">
          <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1, ...tabular,
            color: it.pct >= 0 ? SUCCESS : DANGER }}>
            {it.pct >= 0 ? '↑' : '↓'}{Math.abs(it.pct)}%
          </div>
          <div style={{ fontSize: 10, color: INK3, marginTop: 3 }}>{it.label}</div>
        </div>
      ))}
    </div>
  );
};

// Resolve a CSS custom property to a concrete hex once (Recharts can paint
// `var(--x)` directly for solid fills, but we need a real colour to build the
// graded aging scale and to tint gradient stops). Falls back to a sane brand
// blue if the variable can't be read (SSR / tests).
const useResolvedColor = (cssVar, fallback) => {
  const ref = useRef(null);
  const [color, setColor] = useState(fallback);
  useEffect(() => {
    try {
      const probe = document.createElement('span');
      probe.style.color = `var(${cssVar})`;
      probe.style.display = 'none';
      document.body.appendChild(probe);
      const resolved = getComputedStyle(probe).color;
      document.body.removeChild(probe);
      if (resolved) setColor(resolved);
    } catch (_) {
      setColor(fallback);
    }
  }, [cssVar, fallback]);
  return [color, ref];
};

// Graded aging palette: a soft amber/grey tint for "Not Yet Due" rising
// through warm tones to a deep red for 90+. Index-aligned to the bucket
// order returned by the backend (not-yet-due, 0-30, 31-60, 61-90, 90+).
const AGING_COLORS = [
  '#94A3B8', // Not Yet Due — slate (not a problem yet)
  '#FCD34D', // 0–30  — amber
  '#FB923C', // 31–60 — orange
  '#F87171', // 61–90 — red
  '#DC2626', // 90+   — deep red
];

// Unit-status mix palette for the Key-Metrics donut. Theme-aware: occupied
// reads the active brand (blue/green/orange), the rest are semantic
// (available = success/green, reserved = warning/amber, maintenance = danger),
// and anything unexpected falls through to a neutral slate. Keyed by the
// canonical unit statuses the occupancy snapshot emits.
const STATUS_COLORS = {
  Occupied: 'var(--c-brand)',
  Available: 'var(--c-success)',
  Reserved: 'var(--c-warning)',
  Maintenance: 'var(--c-danger)',
};
const STATUS_FALLBACK = '#94A3B8'; // slate — "Other" / unknown statuses
// Display order for the donut + legend.
const STATUS_ORDER = ['Occupied', 'Available', 'Reserved', 'Maintenance'];

// "2026-06" -> a short percent-axis-friendly label reusing monthLabel below.

// — Shared card chrome (matches Dashboard.js Card) ————————————————————
// `fill` makes the card stretch to its parent's full height (used when the
// chart lives inside a react-grid-layout cell, which sizes the cell for us);
// the standalone grid below still passes a minHeight via `minH`.
const ChartCard = ({ title, chip, right, children, minH = 240, fill = false }) => (
  <div
    className={`rounded-xl border bg-white p-5 flex flex-col ${fill ? 'h-full min-h-0' : ''}`}
    style={{ background: SURFACE, borderColor: BORDER,
      ...(fill ? {} : { minHeight: minH }) }}
  >
    <div className="flex items-center gap-2 mb-3 flex-shrink-0">
      <h3 style={{ fontSize: 13, fontWeight: 500, color: INK }}>{title}</h3>
      {chip && (
        <span
          style={{ fontSize: 10, color: INK3, background: BG,
            borderRadius: 10, padding: '2px 8px' }}
        >
          {chip}
        </span>
      )}
      <div className="flex-1" />
      {right}
    </div>
    <div className="flex-1 min-h-0">{children}</div>
  </div>
);

const TrendChip = ({ pct, label }) => {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <span
      style={{ fontSize: 11, fontWeight: 500, ...tabular,
        color: up ? SUCCESS : DANGER }}
      title={`${label}: ${up ? '+' : ''}${pct}%`}
    >
      {up ? '↑' : '↓'}{Math.abs(pct)}%
      <span style={{ color: INK3, marginLeft: 4, fontWeight: 400 }}>{label}</span>
    </span>
  );
};

const Spinner = () => (
  <div className="flex items-center justify-center h-full" style={{ minHeight: 160 }}>
    <div
      className="w-5 h-5 rounded-full animate-spin"
      style={{ border: `2px solid ${INK}`, borderTopColor: 'transparent' }}
    />
  </div>
);

const Empty = ({ text }) => (
  <div
    className="flex items-center justify-center h-full text-center"
    style={{ minHeight: 160, fontSize: 12, color: INK3 }}
  >
    {text}
  </div>
);

// — Custom tooltips ————————————————————————————————————————————————
const ttBox = {
  borderRadius: 8, border: `0.5px solid ${BORDER}`, background: SURFACE,
  fontSize: 12, padding: '6px 10px', boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
};

const RevenueTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={ttBox}>
      <div style={{ color: INK, fontWeight: 500, marginBottom: 2 }}>
        {monthLabel(p.month)}
      </div>
      <div style={{ color: INK3, ...tabular }}>{fmtMoneyExact(p.revenue)}</div>
    </div>
  );
};

const AgingTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={ttBox}>
      <div style={{ color: INK, fontWeight: 500, marginBottom: 2 }}>
        {p.bucket}
      </div>
      <div style={{ color: INK3, ...tabular }}>
        {fmtMoneyExact(p.amount)} · {p.count} tenant{p.count === 1 ? '' : 's'}
      </div>
    </div>
  );
};

// — Revenue history bar chart ————————————————————————————————————————
const RevenueHistoryCard = ({ data, loading, fill = false, onBarClick }) => {
  const months = data?.months || [];
  const hasData = months.some((m) => (m.revenue || 0) > 0);
  const latest = months[months.length - 1];
  const currentKey = latest?.month;
  // The PREVIOUS month's figure top-right (owner, 2026-09-01): the current
  // month is mid-collection — on the 1st its bar is near zero — so the
  // meaningful "latest revenue" is the last completed month, named so the
  // label carries which month it is.
  const prior = months.length >= 2 ? months[months.length - 2] : null;
  const figStyle = { fontSize: 11, fontWeight: 500, color: INK, ...tabular };
  const priorFigure = prior ? (
    <>
      {fmtMoneyExact(prior.revenue)}
      <span style={{ color: INK3, marginLeft: 4, fontWeight: 400 }}>
        {monthLabel(prior.month)}
      </span>
    </>
  ) : null;
  return (
    <ChartCard
      title="Revenue"
      chip="12 months"
      fill={fill}
      // Clicking the figure drills the month it SHOWS (owner, 2026-09-01:
      // it used to bubble into the whole-card current-month drill — an
      // "Aug" number opening September's popup). stopPropagation keeps the
      // widget wrapper's drill out of it.
      right={
        hasData && prior ? (
          onBarClick ? (
            <button
              type="button"
              title={`Open ${monthLabelFull(prior.month)} revenue`}
              onClick={(e) => { e.stopPropagation(); onBarClick(prior); }}
              style={{ ...figStyle, background: 'none', border: 'none',
                padding: 0, cursor: 'pointer' }}
            >
              {priorFigure}
            </button>
          ) : (
            <span style={figStyle}>{priorFigure}</span>
          )
        ) : null
      }
    >
      {loading ? (
        <Spinner />
      ) : !hasData ? (
        <Empty text="No revenue recorded yet" />
      ) : (
        <div className="flex flex-col h-full">
        <div className="flex-1" style={{ minHeight: fill ? 0 : 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={months} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}
            onClick={(state, event) => {
              // Best-effort fallback for clicks in the plot area NEAR a bar
              // (only when the running recharts hands chart state here — 3.x
              // often does not, which is why the per-Cell DOM handler below
              // is the reliable path). No state → no-op → the click bubbles
              // to the whole-card current-month drill, which is right for
              // off-bar clicks.
              const bar = state?.activePayload?.[0]?.payload;
              if (bar && onBarClick) {
                event?.stopPropagation?.();
                onBarClick(bar);
              }
            }}>
            <defs>
              <linearGradient id="revBar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={BRAND} stopOpacity={0.95} />
                <stop offset="100%" stopColor={BRAND} stopOpacity={0.55} />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false} stroke={BORDER} strokeDasharray="2 4" />
            <XAxis
              dataKey="month" tickFormatter={monthLabel} tickLine={false}
              axisLine={false} tickMargin={8} interval="preserveStartEnd"
              tick={{ fill: INK3, fontSize: 10 }} />
            <YAxis
              tickLine={false} axisLine={false} width={48}
              tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)}
              tick={{ fill: INK3, fontSize: 10 }} />
            <Tooltip content={<RevenueTooltip />} cursor={{ fill: BG }} />
            <Bar
              dataKey="revenue" fill="url(#revBar)" radius={[6, 6, 0, 0]}
              maxBarSize={34} animationDuration={700}
              cursor={onBarClick ? 'pointer' : 'default'}>
              {months.map((m, i) => (
                <Cell
                  key={m.month}
                  // Emphasise the current (latest) month with full-opacity brand.
                  fillOpacity={i === months.length - 1 ? 1 : 0.85}
                  cursor={onBarClick ? 'pointer' : 'default'}
                  // DOM-level click on the bar rect itself: recharts 3.x does
                  // not hand chart state to the chart-level onClick the way
                  // 2.x did, so the state-based path silently missed and every
                  // click fell through to the whole-card (current-month) drill
                  // — the 2026-09-01 live bug. `m` is the mapped month, so the
                  // drilled month can't diverge from the clicked bar.
                  onClick={onBarClick ? (e) => {
                    e.stopPropagation();
                    onBarClick(m);
                  } : undefined}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        </div>
        <ComparisonFooter items={[
          { pct: data?.mom_delta_pct,
            label: currentKey ? `From ${monthLabelFull(priorMonthKey(currentKey))}` : 'vs last month' },
          { pct: data?.yoy_delta_pct,
            label: currentKey ? `From ${monthLabelFull(yearAgoKey(currentKey))}` : 'vs last year' },
        ]} />
        </div>
      )}
    </ChartCard>
  );
};

// — Aging donut ————————————————————————————————————————————————————
// `onMadeClick` / `onDueClick` drill the "$X made" / "$Y due" figures in the
// header into the revenue / past-due breakdown modals. Each is rendered as a
// button when its callback is supplied (cursor-pointer + hover), else inert.
const AgingDonutCard = ({
  data, loading, onSegmentClick, fill = false, revenue,
  onMadeClick, onDueClick,
}) => {
  // The legend is what an operator actually reads — the donut slices are
  // small and several buckets are often $0. Both open the same rows.
  const legendClick = (b) => (e) => {
    e.stopPropagation();
    onSegmentClick?.(b);
  };
  const buckets = data?.buckets || [];
  const total = data?.total_outstanding ?? 0;
  const pastDue = data?.total_past_due ?? 0;
  const segments = buckets.filter((b) => (b.amount || 0) > 0);
  const hasData = segments.length > 0;
  // Render a header figure as a clickable button (when a handler is given)
  // or a plain span (edit mode / no handler) — same look either way.
  const Figure = ({ value, label, color, onClick }) => {
    const inner = (
      <>
        <span style={{ fontSize: 16 }}>{fmtMoney(value)}</span>
        <span style={{ fontSize: 10, fontWeight: 500, marginLeft: 3, opacity: 0.8 }}>
          {label}
        </span>
      </>
    );
    if (!onClick) {
      return <span style={{ color, fontWeight: 600 }}>{inner}</span>;
    }
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick(e); }}
        aria-label={`Show the records behind ${fmtMoney(value)} ${label}`}
        className="cursor-pointer transition-opacity hover:opacity-70"
        // A fingertip, not a pointer. The digits are 24px tall and the card
        // around them drills the OPPOSITE list, so on the phone a tap a few
        // pixels off "$4,172 made" opened who had NOT paid (owner,
        // 2026-10-06). Pad the hit area to the 44px touch minimum and pull
        // it back with negative margins so the header looks exactly as it
        // did. 4px sideways leaves an inert gap between "made" and "due" —
        // a tap between two opposite answers must not guess.
        style={{ color, fontWeight: 600, background: 'none', border: 'none',
          padding: '10px 4px', margin: '-10px -4px', minHeight: 44 }}
      >
        {inner}
      </button>
    );
  };
  return (
    <ChartCard
      title="Payment due"
      chip="aging"
      fill={fill}
      right={
        // The figures' strip. A tap that lands beside or between the
        // figures stops HERE, inert, instead of falling through to the card
        // (whose own tap drills past-due). The extra padding, pulled back by
        // negative margins, is the absorb zone — a near-miss does nothing
        // rather than answering the opposite question. The donut, legend and
        // title keep the card's drill as before.
        <div
          className="flex items-center gap-4"
          style={{ ...tabular, padding: '10px 0 10px 10px',
            margin: '-10px 0 -10px -10px' }}
          onClick={(e) => e.stopPropagation()}
        >
          {revenue != null && (
            <Figure
              value={revenue}
              label="made"
              color="var(--c-success)"
              onClick={onMadeClick}
            />
          )}
          {pastDue > 0 && (
            <Figure
              value={pastDue}
              label="due"
              color={DANGER}
              onClick={onDueClick}
            />
          )}
        </div>
      }
    >
      {loading ? (
        <Spinner />
      ) : !hasData ? (
        <Empty text="No outstanding balances" />
      ) : (
        <div className="flex items-center gap-2 h-full min-h-0">
          {/* Donut fills the cell height (percentage radii) so it never
              overflows a short tile and bleeds over the title. */}
          <div className="relative h-full" style={{ width: 200, flexShrink: 0, minHeight: 170 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={segments}
                  dataKey="amount"
                  nameKey="bucket"
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="96%"
                  paddingAngle={2}
                  stroke={SURFACE}
                  strokeWidth={2}
                  animationDuration={700}
                  onClick={(seg) => onSegmentClick?.(seg?.payload)}
                >
                  {segments.map((b) => {
                    const idx = (data.buckets || []).indexOf(b);
                    return (
                      <Cell
                        key={b.key}
                        fill={AGING_COLORS[idx] || AGING_COLORS[AGING_COLORS.length - 1]}
                        style={{ cursor: onSegmentClick ? 'pointer' : 'default',
                          outline: 'none' }}
                      />
                    );
                  })}
                </Pie>
                <Tooltip content={<AgingTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            {/* Total $ in the donut centre */}
            <div
              className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
            >
              <span style={{ fontSize: 10, color: INK3 }}>Total due</span>
              <span
                style={{ fontSize: 20, fontWeight: 500, color: INK, lineHeight: 1.1, ...tabular }}
              >
                {fmtMoney(total)}
              </span>
            </div>
          </div>
          {/* Legend with $ per bucket */}
          <ul className="flex-1 min-w-0">
            {buckets.map((b) => {
              const idx = (data.buckets || []).indexOf(b);
              return (
                <li
                  key={b.key}
                  className={`flex items-center gap-2 rounded ${
                    onSegmentClick ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                  style={{ paddingBlock: 4 }}
                  onClick={onSegmentClick ? legendClick(b) : undefined}
                  title={onSegmentClick ? `See who makes up ${b.bucket}` : undefined}
                >
                  <span
                    className="rounded-full shrink-0"
                    style={{ width: 8, height: 8,
                      background: AGING_COLORS[idx] || AGING_COLORS[4] }}
                  />
                  <span
                    className="flex-1 truncate"
                    style={{ fontSize: 11, color: INK3 }}
                  >
                    {b.bucket}
                  </span>
                  <span
                    style={{ fontSize: 11, fontWeight: 500, color: INK, ...tabular }}
                  >
                    {fmtMoney(b.amount)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </ChartCard>
  );
};

// — Occupancy gauge (current state only — no history stored) ——————————
const OccupancyGaugeCard = ({ occupancy, loading, fill = false }) => {
  const pct = occupancy?.occupancy_rate ?? 0;
  const occupied = occupancy?.occupied_units ?? 0;
  const total = occupancy?.total_units ?? 0;
  const available = occupancy?.available_units ?? 0;
  const [brand] = useResolvedColor('--c-brand', '#2563EB');
  const trackColor = 'var(--c-bg-soft)';

  const gaugeData = useMemo(
    () => [{ name: 'occupancy', value: Math.max(0, Math.min(100, pct)), fill: brand }],
    [pct, brand],
  );

  return (
    <ChartCard title="Occupancy" chip="current" fill={fill}>
      {loading ? (
        <Spinner />
      ) : total === 0 ? (
        <Empty text="No units configured" />
      ) : (
        <div className="flex items-center gap-2 h-full min-h-0">
          <div className="relative h-full" style={{ width: 200, flexShrink: 0, minHeight: 170 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                innerRadius="72%"
                outerRadius="100%"
                data={gaugeData}
                startAngle={90}
                endAngle={-270}
                barSize={14}
              >
                <RadialBar
                  background={{ fill: trackColor }}
                  dataKey="value"
                  cornerRadius={8}
                  animationDuration={800}
                  fill={brand}
                />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span
                style={{ fontSize: 28, fontWeight: 500, color: INK, lineHeight: 1, ...tabular }}
              >
                {pct}%
              </span>
              <span style={{ fontSize: 10, color: INK3, marginTop: 2 }}>occupied</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="flex justify-between items-center"
              style={{ paddingBlock: 4 }}
            >
              <span style={{ fontSize: 11, color: INK3 }}>Occupied</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: INK, ...tabular }}>
                {occupied}
              </span>
            </div>
            <div
              className="flex justify-between items-center"
              style={{ paddingBlock: 4 }}
            >
              <span style={{ fontSize: 11, color: INK3 }}>Available</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: INK, ...tabular }}>
                {available}
              </span>
            </div>
            <div
              className="flex justify-between items-center"
              style={{ paddingBlock: 4, borderTop: `0.5px solid ${BORDER}`, marginTop: 4 }}
            >
              <span style={{ fontSize: 11, color: INK3 }}>Total units</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: INK, ...tabular }}>
                {total}
              </span>
            </div>
          </div>
        </div>
      )}
    </ChartCard>
  );
};

// — Key Metrics donut (unit-status mix) ———————————————————————————————
// Sourced from occupancy-snapshot `by_status` (a {status: count} map). Renders
// a donut of the unit-status mix with the total unit count in the centre and a
// legend with per-status counts. `onSliceClick(status)` drills into the units
// of that status (the dashboard wires it to the unit_status breakdown modal).
const StatusTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={ttBox}>
      <div style={{ color: INK, fontWeight: 500, marginBottom: 2 }}>
        {p.status}
      </div>
      <div style={{ color: INK3, ...tabular }}>
        {p.count} unit{p.count === 1 ? '' : 's'}
        {p.pct != null ? ` · ${p.pct}%` : ''}
      </div>
    </div>
  );
};

const KeyMetricsCard = ({ occupancy, loading, fill = false, onSliceClick }) => {
  // by_status is the canonical {status: count} map from occupancy-snapshot.
  // Memoize so the `|| {}` fallback doesn't mint a new object every render
  // (which would bust the slices useMemo below on every render).
  const byStatus = useMemo(() => occupancy?.by_status || {}, [occupancy?.by_status]);
  // Build slices in a stable order: known statuses first, then any extras
  // collapsed under "Other" so an unexpected status still shows up honestly.
  const slices = useMemo(() => {
    const known = STATUS_ORDER
      .filter((s) => (byStatus[s] || 0) > 0)
      .map((s) => ({ status: s, count: byStatus[s], color: STATUS_COLORS[s] }));
    let otherCount = 0;
    Object.entries(byStatus).forEach(([s, c]) => {
      if (!STATUS_ORDER.includes(s) && (c || 0) > 0) otherCount += c;
    });
    if (otherCount > 0) {
      known.push({ status: 'Other', count: otherCount, color: STATUS_FALLBACK });
    }
    return known;
  }, [byStatus]);

  const total = useMemo(
    () => slices.reduce((sum, s) => sum + (s.count || 0), 0),
    [slices],
  );
  const withPct = slices.map((s) => ({
    ...s,
    pct: total ? Math.round((s.count / total) * 100) : 0,
  }));
  const hasData = total > 0;

  return (
    <ChartCard title="Key metrics" chip="unit mix" fill={fill}>
      {loading ? (
        <Spinner />
      ) : !hasData ? (
        <Empty text="No units configured" />
      ) : (
        <div className="flex items-center gap-2 h-full">
          <div className="relative" style={{ width: 168, height: 200, flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={withPct}
                  dataKey="count"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  innerRadius={56}
                  outerRadius={82}
                  paddingAngle={2}
                  stroke={SURFACE}
                  strokeWidth={2}
                  animationDuration={700}
                  onClick={(seg) => onSliceClick?.(seg?.payload?.status)}
                >
                  {withPct.map((s) => (
                    <Cell
                      key={s.status}
                      fill={s.color || STATUS_FALLBACK}
                      style={{ cursor: onSliceClick ? 'pointer' : 'default',
                        outline: 'none' }}
                    />
                  ))}
                </Pie>
                <Tooltip content={<StatusTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            {/* Total units in the donut centre */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span
                style={{ fontSize: 24, fontWeight: 500, color: INK, lineHeight: 1, ...tabular }}
              >
                {total}
              </span>
              <span style={{ fontSize: 10, color: INK3, marginTop: 2 }}>units</span>
            </div>
          </div>
          {/* Legend with per-status counts */}
          <ul className="flex-1 min-w-0">
            {withPct.map((s) => (
              <li
                key={s.status}
                className="flex items-center gap-2"
                style={{ paddingBlock: 4 }}
              >
                <span
                  className="rounded-full shrink-0"
                  style={{ width: 8, height: 8, background: s.color || STATUS_FALLBACK }}
                />
                <span
                  className="flex-1 truncate"
                  style={{ fontSize: 11, color: INK3 }}
                >
                  {s.status}
                </span>
                <span
                  style={{ fontSize: 11, fontWeight: 500, color: INK, ...tabular }}
                >
                  {s.count}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </ChartCard>
  );
};

// — Occupancy trend (reconstructed % over the last ~12 month-ends) ————————
const OccupancyTrendTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={ttBox}>
      <div style={{ color: INK, fontWeight: 500, marginBottom: 2 }}>
        {monthLabel(p.month)}
      </div>
      <div style={{ color: INK3, ...tabular }}>
        {p.occupancy_pct}% · {p.occupied}/{p.total} units
      </div>
    </div>
  );
};

const OccupancyTrendCard = ({
  data, loading, fill = false,
  moveIns, moveOuts, onMoveInClick, onMoveOutClick,
}) => {
  const months = data?.series || data?.months || [];
  const [brand] = useResolvedColor('--c-brand', '#2563EB');
  // "Has data" = any month with a non-zero occupancy (an early facility with
  // all-zero months is an honest empty trend, not a fabricated flat line).
  const hasData = months.some((m) => (m.occupancy_pct || 0) > 0);
  const latest = months.length ? months[months.length - 1].occupancy_pct : 0;
  // MoM / YoY as percentage-point deltas off the reconstructed series — shown
  // on the right of the footer (move-ins/outs sit on the left).
  const prior = months.length >= 2 ? months[months.length - 2] : null;
  const yearAgo = months.length >= 13 ? months[months.length - 13] : null;
  const momDelta = prior ? Math.round(latest - prior.occupancy_pct) : null;
  const yoyDelta = yearAgo ? Math.round(latest - yearAgo.occupancy_pct) : null;
  const comps = [
    { pct: momDelta, label: prior ? `From ${monthLabelFull(prior.month)}` : null },
    { pct: yoyDelta, label: yearAgo ? `From ${monthLabelFull(yearAgo.month)}` : null },
  ].filter((c) => c.pct != null);
  return (
    <ChartCard
      title="Occupancy trend"
      chip="12 months"
      fill={fill}
      right={
        hasData ? (
          <span style={{ fontSize: 11, fontWeight: 500, color: INK, ...tabular }}>
            {latest}%
            <span style={{ color: INK3, marginLeft: 4, fontWeight: 400 }}>now</span>
          </span>
        ) : null
      }
    >
      {loading ? (
        <Spinner />
      ) : !hasData ? (
        <Empty text="No occupancy history yet" />
      ) : (
        <div className="flex flex-col h-full">
        <div className="flex-1" style={{ minHeight: fill ? 0 : 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={months} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <defs>
              <linearGradient id="occTrendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={brand} stopOpacity={0.28} />
                <stop offset="100%" stopColor={brand} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke={BORDER} strokeDasharray="2 4" />
            <XAxis
              dataKey="month" tickFormatter={monthLabel} tickLine={false}
              axisLine={false} tickMargin={8} interval="preserveStartEnd"
              tick={{ fill: INK3, fontSize: 10 }} />
            <YAxis
              domain={[0, 100]} tickLine={false} axisLine={false} width={36}
              tickFormatter={(v) => `${v}%`}
              tick={{ fill: INK3, fontSize: 10 }} />
            <Tooltip content={<OccupancyTrendTooltip />} cursor={{ stroke: BORDER }} />
            <Area
              type="monotone" dataKey="occupancy_pct"
              stroke={brand} strokeWidth={1.75}
              fill="url(#occTrendFill)" dot={false}
              activeDot={{ r: 4, fill: brand, strokeWidth: 0 }}
              animationDuration={700} />
          </AreaChart>
        </ResponsiveContainer>
        </div>
        {/* Footer: this month's move-ins / move-outs on the LEFT (drillable;
            stopPropagation so the whole-card occupancy drill doesn't fire),
            the MoM / YoY occupancy comparison on the RIGHT. */}
        <div
          className="flex items-center justify-between flex-shrink-0"
          style={{ marginTop: 6, paddingTop: 8, borderTop: `0.5px solid ${BORDER}`, ...tabular }}
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onMoveInClick ? (e) => { e.stopPropagation(); onMoveInClick(); } : undefined}
              aria-label="Show this month's move-in rentals"
              className="flex items-baseline gap-1.5 rounded-lg"
              style={{ background: 'var(--c-success-bg)', padding: '3px 9px',
                border: 0, cursor: onMoveInClick ? 'pointer' : 'default' }}
            >
              <span style={{ fontSize: 16, fontWeight: 600, color: SUCCESS, lineHeight: 1 }}>
                {moveIns ?? 0}
              </span>
              <span style={{ fontSize: 10, fontWeight: 500, color: SUCCESS, opacity: 0.85 }}>
                move-ins
              </span>
            </button>
            <button
              type="button"
              onClick={onMoveOutClick ? (e) => { e.stopPropagation(); onMoveOutClick(); } : undefined}
              aria-label="Show this month's move-out rentals"
              className="flex items-baseline gap-1.5 rounded-lg"
              style={{ background: 'var(--c-danger-bg)', padding: '3px 9px',
                border: 0, cursor: onMoveOutClick ? 'pointer' : 'default' }}
            >
              <span style={{ fontSize: 16, fontWeight: 600, color: DANGER, lineHeight: 1 }}>
                {moveOuts ?? 0}
              </span>
              <span style={{ fontSize: 10, fontWeight: 500, color: DANGER, opacity: 0.85 }}>
                move-outs
              </span>
            </button>
          </div>
          {comps.length > 0 && (
            <div className="flex items-center gap-3">
              {comps.map((it, i) => (
                <div key={i} className="text-center">
                  <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1, ...tabular,
                    color: it.pct >= 0 ? SUCCESS : DANGER }}>
                    {it.pct >= 0 ? '↑' : '↓'}{Math.abs(it.pct)}%
                  </div>
                  {it.label && (
                    <div style={{ fontSize: 9, color: INK3, marginTop: 2 }}>{it.label}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      )}
    </ChartCard>
  );
};

// — Self-fetching grid widgets ————————————————————————————————————————
//
// Each chart is now a first-class react-grid-layout cell on the Dashboard,
// so the three are exported as standalone, height-filling widgets. They
// self-fetch (revenue history / aging buckets) so a saved layout can drop
// any subset onto the grid without the parent pre-wiring the data. The
// occupancy gauge takes the already-loaded snapshot from the dashboard
// overview (no extra round-trip) and falls back to fetching it if absent.

/** Revenue history bar chart — fills its grid cell. When `onClick` is given
 *  the WHOLE card is drillable (like the occupancy trend); a click on/near a
 *  BAR wins and drills that bar's month (the chart stops propagation), so one
 *  click still opens exactly one drill — the right one. */
export const RevenueBarWidget = ({ facilityId, onBarClick, onClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!facilityId) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    dashboardService.getRevenueHistory(facilityId, 12)
      .then((r) => { if (!cancelled) setData(r?.data || null); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [facilityId]);
  const card = (
    <RevenueHistoryCard
      data={data} loading={loading} fill
      onBarClick={onBarClick}
    />
  );
  if (!onClick) return card;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
      className="h-full cursor-pointer"
    >
      {card}
    </div>
  );
};

/**
 * Aging donut — fills its grid cell. `onAgingSegment({key})` drills a bucket
 * down; `onMadeClick` / `onDueClick` drill the header "$X made" / "$Y due"
 * figures into the revenue / past-due breakdowns.
 */
export const AgingDonutWidget = ({
  facilityId, onAgingSegment, revenue, onMadeClick, onDueClick, onClick,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!facilityId) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    dashboardService.getAgingBuckets(facilityId)
      .then((r) => { if (!cancelled) setData(r?.data || null); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [facilityId]);
  const card = (
    <AgingDonutCard
      data={data}
      loading={loading}
      // Segments are clickable whether the whole card drills (onClick) or a
      // per-bucket handler is given — so the donut shows a pointer cursor and
      // a segment click always drills. (made/due figures stopPropagation.)
      onSegmentClick={onAgingSegment || onClick}
      revenue={revenue}
      onMadeClick={onMadeClick}
      onDueClick={onDueClick}
      fill
    />
  );
  if (!onClick) return card;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
      className="h-full cursor-pointer"
    >
      {card}
    </div>
  );
};

/**
 * Occupancy gauge — fills its grid cell. Prefers the `occupancy` snapshot
 * already loaded by the dashboard overview; fetches it only if not supplied
 * (e.g. the widget is mounted before the overview resolves).
 */
export const OccupancyGaugeWidget = ({ facilityId, occupancy, onDrill }) => {
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(!occupancy);
  useEffect(() => {
    if (occupancy || !facilityId) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    dashboardService.getOccupancySnapshot(facilityId)
      .then((r) => { if (!cancelled) setFetched(r?.data || null); })
      .catch(() => { if (!cancelled) setFetched(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [facilityId, occupancy]);
  const card = (
    <OccupancyGaugeCard occupancy={occupancy || fetched} loading={loading} fill />
  );
  if (!onDrill) return card;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onDrill}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDrill(); }
      }}
      className="h-full cursor-pointer"
    >
      {card}
    </div>
  );
};

/**
 * Key Metrics donut — unit-status mix. Fills its grid cell. Prefers the
 * `occupancy` snapshot already loaded by the dashboard overview (no extra
 * round-trip); fetches it only when not supplied. `onSliceClick(status)`
 * drills into the units of the clicked status.
 */
export const KeyMetricsWidget = ({ facilityId, occupancy, onSliceClick }) => {
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(!occupancy);
  useEffect(() => {
    if (occupancy || !facilityId) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    dashboardService.getOccupancySnapshot(facilityId)
      .then((r) => { if (!cancelled) setFetched(r?.data || null); })
      .catch(() => { if (!cancelled) setFetched(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [facilityId, occupancy]);
  return (
    <KeyMetricsCard
      occupancy={occupancy || fetched}
      loading={loading}
      onSliceClick={onSliceClick}
      fill
    />
  );
};

/**
 * Occupancy trend area chart — fills its grid cell. Self-fetches the
 * reconstructed monthly occupancy % series (real rental start/end dates).
 * `onClick` (whole-card) drills into the current occupancy breakdown.
 */
// Fetch 13 month-ends (not 12) so the footer can show a true year-ago (same
// month last year) comparison once the facility has that much history.
export const OccupancyTrendWidget = ({
  facilityId, months = 13, onClick,
  moveIns, moveOuts, onMoveInClick, onMoveOutClick,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!facilityId) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    dashboardService.getOccupancyTrend(facilityId, months)
      .then((r) => { if (!cancelled) setData(r?.data || null); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [facilityId, months]);
  const card = (
    <OccupancyTrendCard
      data={data} loading={loading} fill
      moveIns={moveIns} moveOuts={moveOuts}
      onMoveInClick={onMoveInClick} onMoveOutClick={onMoveOutClick}
    />
  );
  if (!onClick) return card;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
      className="h-full cursor-pointer"
    >
      {card}
    </div>
  );
};
